import { Component, Inject, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { IStateParamsService, IStateService } from 'angular-ui-router';
import { every, findLast, keys, values } from 'lodash';
import { take } from 'rxjs/operators';
import { getPreviousStates } from '../../../store/selectors/previous-states-selectors';
import { unsubscribeComponent } from '../../helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { ModalsService } from '../../modals/modals.service';
import { IStateGoOpts } from '../../services/prev-state/prev-state.service';

/* istanbul ignore next */
@Component({
  selector: 'exit-view',
  template: require('./exit-view.component.html'),
  styles: [require('./exit-view.component.scss')],
})
@unsubscribeComponent
export class ExitViewComponent {
  @Input() ssvExitState?: IStateGoOpts; // Go back to previous view in SSV
  @Input() warningTitle?: string; // opens modal with warning message if present
  @Input() warningMessage?: string; // opens modal with warning message if present
  @Input() icon?: string = 'close'; // which icon is displayed

  constructor(
    @Inject('$state') private $state: IStateService,
    @Inject('$stateParams') private $stateParams: IStateParamsService,
    @Inject('SpinnerModal') private spinnerModal,
    private store: Store<any>,
    private modalsService: ModalsService,
  ) {}

  private goToState(name, params) {
    this.$state.go(name, params).then(() => {
      setTimeout(() => {
        this.spinnerModal.deactivateSpinner();
      });
    });
  }

  private goHome() {
    const name = 'main.school.home';
    const params = { schoolId: this.$stateParams.schoolId };

    this.goToState(name, params);
  }

  private goToLastNonProfileOrGridState(previousStates) {
    // remove current state from options, relevant when one exit view
    // component leads to another (JE)
    const currentState = this.getCurrentState();
    const STATES_TO_EXCLUDE = ['single-student', 'profile', 'grid', 'settings', currentState];
    const stateToGoTo = findLast(previousStates, (stateGoOpts: IStateGoOpts) => {
      const name = keys(stateGoOpts)[0];
      return every(STATES_TO_EXCLUDE, stateToExclude => {
        return !name.includes(stateToExclude);
      });
    });
    if (stateToGoTo) {
      const name = keys(stateToGoTo)[0];
      const params = values(stateToGoTo)[0];

      this.goToState(name, params);
    } else {
      this.goHome();
    }
  }

  public goBack(): void {
    if (this.warningMessage || this.warningTitle) {
      this.openWarningModal(this.warningTitle, this.warningMessage);
    } else if (this.ssvExitState) {
      this.$state.go(this.ssvExitState.name, this.ssvExitState.params);
    } else {
      this.store
        .select(getPreviousStates)
        .pipe(take(1))
        .subscribe(previousStates => {
          const previousStatesLength = previousStates.length;
          const previousState = previousStatesLength && previousStates[previousStatesLength - 1];
          if (previousState) {
            this.goToLastNonProfileOrGridState(previousStates);
          } else {
            this.goHome();
          }
        });
    }
  }

  public openWarningModal(title, message): void {
    const data = {
      title,
      message,
      confirmText: 'Yes',
    };
    this.modalsService
      .openConfirmModal(data, 'med')
      .afterClosed()
      .subscribe((confirmed: false | true) => {
        if (confirmed) {
          this.store
            .select(getPreviousStates)
            .pipe(take(1))
            .subscribe(previousStates => {
              const previousStatesLength = previousStates.length;
              const previousState = previousStatesLength && previousStates[previousStatesLength - 1];

              previousState ? this.goToLastNonProfileOrGridState(previousStates) : this.goHome();
            });
        }
      });
  }

  public getCurrentState(): string {
    return this.$state.current.name;
  }
}
